import { useState, useRef, useLayoutEffect } from "react";
import { Users, Library, User, Briefcase, Radio, Star } from "lucide-react";
import Reveal from "@/components/Reveal";

/* Stats — Figma node 102:3000 (Frame 101), 1220 × 355.58.

   Scaled stage rather than a fluid layout: the design is a fixed-width block that
   grows with the viewport, and rendering it 1:1 left the heading and circles
   noticeably undersized on wide screens. Below lg it becomes a responsive grid. */

const STAGE_W = 1220;
const STAGE_H = 356;

// Tiles 4 and 6 exported from Figma as flattened SVGs, so those two circle fills
// are sampled approximations.
const STATS = [
  { value: "10K+",  label: "Active Learners",    circle: "#FFFF91", icon: Users },
  { value: "25+",   label: "High Income Skills", circle: "#BEC4F4", icon: Library },
  { value: "5000+", label: "Community Members",  circle: "#FACCE4", icon: User },
  { value: "100+",  label: "Countries Reached",  circle: "#A8EFE0", icon: Briefcase },
  { value: "2M+",   label: "Content Reach",      circle: "#FC80F4", icon: Radio },
  { value: "4.9/5", label: "Student Rating",     circle: "#56C7F2", icon: Star },
];

const TILE_X = [19, 231, 443, 655, 867, 1079]; // 122 wide, 90 gap
const TILE_Y = 168.81;

function Tile({ s }) {
  return (
    <>
      <div
        className="absolute flex items-center justify-center rounded-full"
        style={{ left: 7.61, top: 0, width: 106.772, height: 106.772, background: s.circle }}
      >
        <s.icon width={48} height={48} className="text-black" strokeWidth={1.5} />
      </div>
      <div
        className="absolute text-center font-montserrat font-semibold text-black"
        style={{ left: -59, top: 117.77, width: 240, fontSize: 34, lineHeight: "41px" }}
      >
        {s.value}
      </div>
      {/* Label box is wider than the tile and offset left so long labels overflow
          evenly to both sides instead of only to the right. */}
      <div
        className="absolute whitespace-nowrap text-center font-poppins text-black"
        style={{ left: -59, top: 162.77, width: 240, fontSize: 16, lineHeight: "24px" }}
      >
        {s.label}
      </div>
    </>
  );
}

export default function StatsSection() {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / STAGE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <section id="stats" className="mx-auto w-[92%] max-w-[1660px] py-12 lg:w-[86%] lg:py-8 xl:py-10">

      {/* ── desktop stage ── */}
      <div
        ref={stageRef}
        className="relative hidden w-full lg:block"
        style={{ height: scale ? STAGE_H * scale : undefined }}
      >
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            width: STAGE_W,
            height: STAGE_H,
            transform: `scale(${scale || 1})`,
            visibility: scale ? "visible" : "hidden",
          }}
        >
          <div
            className="absolute rounded-[17.812px] bg-[var(--lime)]"
            style={{ left: 289.42, top: 0, width: 315, height: 69.354, transform: "rotate(0.5deg)" }}
            aria-hidden
          />
          <h2
            className="absolute text-center font-poppins font-medium text-black"
            style={{ left: 191.5, top: 4.81, width: 837, fontSize: 40, lineHeight: "64.463px" }}
          >
            Join&nbsp;&nbsp; <span className="font-semibold">10K+ Learners</span>&nbsp;&nbsp; Building Their Future
          </h2>
          <p
            className="absolute text-center font-poppins font-light text-black"
            style={{ left: 191.5, top: 89.81, width: 837, fontSize: 18, lineHeight: "31px" }}
          >
            One ecosystem. Endless possibilities.
          </p>

          {STATS.map((s, i) => (
            <div key={s.label} className="absolute" style={{ left: TILE_X[i], top: TILE_Y, width: 122, height: 186.77 }}>
              <Tile s={s} />
            </div>
          ))}
        </div>
      </div>

      {/* ── below lg: responsive grid ── */}
      <div className="lg:hidden">
        <Reveal className="mx-auto flex max-w-[837px] flex-col items-center gap-4 text-center">
          <h2 className="font-poppins text-[30px] font-medium leading-[1.5] text-black sm:text-[36px]">
            Join{" "}
            <span className="relative mx-[0.5em] inline-block">
              <span
                className="absolute -inset-x-[0.25em] inset-y-0 rounded-[0.42em] bg-[var(--lime)]"
                style={{ transform: "rotate(0.5deg)" }}
                aria-hidden
              />
              <span className="relative font-semibold">10K+ Learners</span>
            </span>{" "}
            Building Their Future
          </h2>
          <p className="font-poppins text-[16px] font-light leading-[28px] text-[var(--fig-body)]">
            One ecosystem. Endless possibilities.
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 gap-3.5 sm:mt-12 sm:grid-cols-3 sm:gap-5">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 70} className="h-full">
              <div className="flex h-full flex-col items-center gap-3 rounded-[20px] border border-black/5 bg-[var(--fig-surface)] px-3 py-6 text-center shadow-[0_6px_20px_0_rgba(0,0,0,0.05)] sm:gap-4 sm:py-7">
                <div
                  className="flex h-[66px] w-[66px] shrink-0 items-center justify-center rounded-full sm:h-[80px] sm:w-[80px]"
                  style={{ background: s.circle }}
                >
                  <s.icon className="h-8 w-8 text-black sm:h-10 sm:w-10" strokeWidth={1.5} />
                </div>
                <p className="font-montserrat text-[26px] font-semibold leading-none text-black sm:text-[30px]">
                  {s.value}
                </p>
                <p className="font-poppins text-[13px] leading-tight text-[var(--fig-body)] sm:text-[15px]">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
